/**
 * Admin Users — Main Config
 * Assembles ADMIN_USERS_CONFIG from field, action, and view sub-modules.
 *
 *   admin-users-core-fields.ts     — editable user fields (name, email, role, billing)
 *   admin-users-metadata-fields.ts — read-only system + integration fields (QB, Meridian)
 *   admin-users-actions.ts         — CRUD flags, detail actions, bulk actions
 *   admin-users-views.ts           — saved views + feature flags
 */

import type { SimpleTableConfig } from "../../shared/UniversalCRMTable/types";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import type { User } from "@/lib/types/user";

import { adminUserCoreFields } from "./admin-users-core-fields";
import { adminUserMetadataFields } from "./admin-users-metadata-fields";
import { adminUserActions, adminUserDetailActions, adminUserBulkActions } from "./admin-users-actions";
import { adminUserSavedViews, adminUserFeatures } from "./admin-users-views";

/**
 * Progressive-disclosure section layout for the Create/Edit Admin User form.
 * See plan `the-log-activity-modal-enumerated-whisper.md`.
 */
const CREATE_ADMIN_USER_FORM_SECTIONS = (): readonly EntityFormSectionConfig[] => [
  {
    id: "core",
    kind: "always",
    fieldNames: ["first_name", "last_name", "email", "role"],
  },
  {
    id: "classification",
    kind: "collapsible",
    title: "Account status",
    summary: "Active flag",
    fieldNames: ["is_active"],
  },
  {
    id: "commercial",
    kind: "collapsible",
    title: "Billing",
    summary: "Availability, billable, rate",
    fieldNames: ["available_hours", "is_billable", "hourly_rate"],
  },
  {
    id: "contact-channels",
    kind: "collapsible",
    summary: "Email aliases",
    fieldNames: ["email_aliases"],
  },
  {
    id: "metadata",
    kind: "collapsible",
    title: "Integrations",
    summary: "Meridian email provider, QuickBooks environment",
    fieldNames: ["meridian_default_email_provider", "quickbooks_environment"],
  },
];

export { adminUserCoreFields } from "./admin-users-core-fields";
export { adminUserMetadataFields } from "./admin-users-metadata-fields";
export { adminUserActions, adminUserDetailActions, adminUserBulkActions } from "./admin-users-actions";
export { adminUserSavedViews, adminUserFeatures } from "./admin-users-views";

export const ADMIN_USERS_CONFIG: SimpleTableConfig<User & Record<string, any>> = {
  entity: "admin_users",
  displayName: "Users",
  // Admin user list has no select-type status field. Explicit opt-out.
  statusFieldId: null,
  defaultPageSize: 10,
  defaultSort: { field: "created_at", direction: "desc" },
  rowActionsPlacement: "inlineTitle",
  rowActionsMaxPrimaryIcons: 2,
  // First Name is the clickable "view details" title. With no `detailModalId`
  // override, clicking it opens the registry-driven detail modal
  // (UniversalDetailModalContainer, modalId "admin_users-detail") — the same
  // chrome/header/navigation contacts use. Tabs come from
  // ADMIN_USER_DETAIL_CONFIG via useAdminUserDetailModalTabs.
  titleFieldId: "first_name",

  /** Card view: identity → role → reachability → status */
  cardFaceFieldOrder: ["first_name", "last_name", "role", "email", "is_active"],

  mobileDetailSections: [
    { id: "basic", label: "Basic Info" },
    { id: "access", label: "Access & Billing" },
    { id: "metadata", label: "Integrations & Metadata" },
  ],

  fields: {
    ...adminUserCoreFields,
    ...adminUserMetadataFields,
  },

  createFormSections: CREATE_ADMIN_USER_FORM_SECTIONS,

  actions: adminUserActions,
  detailActions: adminUserDetailActions,
  bulkActions: adminUserBulkActions,
  features: adminUserFeatures,
  savedViews: adminUserSavedViews,
};
